import React, { useCallback, useEffect, useState } from "react";
import {
    obterPessoasAcessoAtivo,
    registrarSaida,
} from "../../../api/requisicoes/registroAcessos";
import { formatarDataHoraBrasilia } from "../../../components/Funcoes";
import Loading from "../../../components/Loading";
import { useAlert } from "../../../context/AlertContext";
import { useConfirm } from "../../../context/ConfirmContext";
import "./style.css";

const normalizarTexto = (valor) =>
    typeof valor === "string"
        ? valor
              .normalize("NFD")
              .replace(/[\u0300-\u036f]/g, "")
              .toLowerCase()
        : "";

const formatarEntrada = (valor) => {
    if (!valor) {
        return "-";
    }

    try {
        return formatarDataHoraBrasilia(valor);
    } catch (erro) {
        console.error("Erro ao formatar data de entrada:", erro);
        return "-";
    }
};

const RegistroSaidaMobile = () => {
    const [registros, setRegistros] = useState([]);
    const [filtro, setFiltro] = useState("");
    const [cartaoExpandidoId, setCartaoExpandidoId] = useState(null);
    const [carregando, setCarregando] = useState(false);
    const [registroProcessandoId, setRegistroProcessandoId] = useState(null);
    const [erroCarregamento, setErroCarregamento] = useState("");

    const { showAlert } = useAlert();
    const { showConfirm } = useConfirm();

    const carregarRegistros = useCallback(async () => {
        setCarregando(true);
        setErroCarregamento("");

        try {
            const dados = await obterPessoasAcessoAtivo();

            if (!Array.isArray(dados)) {
                throw new Error("Resposta inválida ao buscar acessos ativos.");
            }

            setRegistros(dados);
        } catch (erro) {
            console.error("Erro ao obter pessoas com acesso ativo:", erro);
            setErroCarregamento(
                "Não foi possível carregar os registros de acesso."
            );
        } finally {
            setCarregando(false);
        }
    }, []);

    useEffect(() => {
        carregarRegistros();
    }, [carregarRegistros]);

    const registrosFiltrados = registros.filter((registro) => {
        const filtroNormalizado = normalizarTexto(filtro);

        return (
            normalizarTexto(registro.nomeCompleto).includes(
                filtroNormalizado
            ) ||
            normalizarTexto(registro.tipoPerfil).includes(filtroNormalizado) ||
            normalizarTexto(registro.responsavel).includes(filtroNormalizado)
        );
    });

    const alternarCartao = (idRegistro) => {
        setCartaoExpandidoId((idAtual) =>
            idAtual === idRegistro ? null : idRegistro
        );
    };

    const registrarSaidaDaPessoa = (registro) => {
        if (!registro.idRegistro || !registro.saidaLiberada) {
            return;
        }

        showConfirm(
            `Deseja registrar a saída de ${registro.nomeCompleto}?`,
            async () => {
                setRegistroProcessandoId(registro.idRegistro);
                setCarregando(true);

                try {
                    await registrarSaida(registro.idRegistro);
                    showAlert("Saída registrada com sucesso!", "sucesso");
                    setCartaoExpandidoId(null);
                    await carregarRegistros();
                } catch (erro) {
                    if (erro.response) {
                        showAlert(
                            `Erro! ${erro.response.data.error}.`,
                            "erro"
                        );
                    } else {
                        showAlert(
                            "Erro desconhecido, contate o administrador do sistema!",
                            "erro"
                        );
                    }
                } finally {
                    setRegistroProcessandoId(null);
                    setCarregando(false);
                }
            },
            () => {}
        );
    };

    return (
        <div className="registroSaidaMobile-tela">
            {carregando && <Loading />}

            <header className="registroSaidaMobile-header">
                <h1>Registro de Saída</h1>
            </header>

            <div className="registroSaidaMobile-conteudo">
                <input
                    type="search"
                    className="registroSaidaMobile-pesquisa"
                    placeholder="Filtrar por nome, perfil ou responsável..."
                    value={filtro}
                    onChange={(evento) => setFiltro(evento.target.value)}
                />

                <div className="registroSaidaMobile-lista">
                    {erroCarregamento ? (
                        <p className="registroSaidaMobile-mensagem">
                            {erroCarregamento}
                        </p>
                    ) : !carregando && registrosFiltrados.length === 0 ? (
                        <p className="registroSaidaMobile-mensagem">
                            Nenhum registro de acesso encontrado.
                        </p>
                    ) : (
                        registrosFiltrados.map((registro) => {
                            const expandido =
                                cartaoExpandidoId === registro.idRegistro;
                            const saidaLiberada = Boolean(
                                registro.saidaLiberada
                            );
                            const processando =
                                registroProcessandoId === registro.idRegistro;

                            return (
                                <div
                                    className="registroSaidaMobile-card"
                                    key={registro.idRegistro}
                                >
                                    <button
                                        type="button"
                                        className="registroSaidaMobile-cardHeader"
                                        onClick={() =>
                                            alternarCartao(registro.idRegistro)
                                        }
                                        aria-expanded={expandido}
                                    >
                                        <span className="registroSaidaMobile-resumo">
                                            <strong>
                                                {registro.nomeCompleto || "-"}
                                            </strong>
                                            <small>
                                                {registro.placaVeiculo ||
                                                    "Sem placa"}
                                            </small>
                                        </span>
                                        <span
                                            className="registroSaidaMobile-status"
                                            role="img"
                                            aria-label={
                                                saidaLiberada
                                                    ? "Saída liberada"
                                                    : "Saída não liberada"
                                            }
                                        >
                                            {saidaLiberada ? "🟢" : "🔴"}
                                        </span>
                                    </button>

                                    {expandido && (
                                        <div className="registroSaidaMobile-detalhes">
                                            <div className="registroSaidaMobile-informacoes">
                                                <p>
                                                    <strong>Entrada:</strong>{" "}
                                                    {formatarEntrada(
                                                        registro.dataHoraEntrada
                                                    )}
                                                </p>
                                                <p>
                                                    <strong>Perfil:</strong>{" "}
                                                    {registro.tipoPerfil || "-"}
                                                </p>
                                                <p>
                                                    <strong>Destino:</strong>{" "}
                                                    {registro.localDestino ||
                                                        "-"}
                                                </p>
                                                <p>
                                                    <strong>Responsável:</strong>{" "}
                                                    {registro.responsavel || "-"}
                                                </p>
                                            </div>

                                            <button
                                                type="button"
                                                className="registroSaidaMobile-botaoRegistrar"
                                                onClick={() =>
                                                    registrarSaidaDaPessoa(
                                                        registro
                                                    )
                                                }
                                                disabled={
                                                    !saidaLiberada || processando
                                                }
                                                aria-label={`Registrar saída de ${
                                                    registro.nomeCompleto ||
                                                    "pessoa"
                                                }`}
                                                title="Registrar Saída"
                                            >
                                                <i
                                                    className="fas fa-sign-out-alt"
                                                    aria-hidden="true"
                                                ></i>
                                            </button>
                                        </div>
                                    )}
                                </div>
                            );
                        })
                    )}
                </div>
            </div>
        </div>
    );
};

export default RegistroSaidaMobile;
